import { useCallback, useMemo } from 'react';
import { matchPath } from 'react-router-dom';
import { useProctorGroups } from '@indeed/frontend-proctor-utils';
import { Flow, FlowStep, SubFlow, SubFlowStep } from '../types';
import GettextFactory from '../../../util/GettextFactory';

/** Match a route against a step's routes, using matchPath for parameterized
 * patterns (e.g. `/mid-setup-feedback/:category`) and exact equality otherwise. */
const stepMatchesRoute = (step: { routes: string[] }, route: string): boolean =>
    step.routes.some((pattern) =>
        pattern.includes(':') ? matchPath(pattern, route) !== null : pattern === route,
    );

/** Stub flow returned when this hook is not active */
const INACTIVE_STUB: {
    flow: Flow;
    subFlows: SubFlow[];
    getNextStep: (currentRoute: string) => Promise<string | null>;
    loading: boolean;
} = {
    flow: {
        id: 'auto-apply-setup',
        name: 'Auto Apply Setup Flow',
        description: 'Set up automatic job applications.',
        steps: [],
    },
    subFlows: [],
    getNextStep: () => Promise.resolve(null),
    loading: false,
};

/**
 * Hook to get the auto-apply-setup flow configuration.
 *
 * @param isActive - Whether this flow is currently active. If false, returns a minimal stub.
 * @returns Object containing flow definition, getNextStep function, and loading state
 */
export const useAutoApplySetupFlow = (
    isActive: boolean = true,
): {
    flow: Flow;
    subFlows: SubFlow[];
    getNextStep: (currentRoute: string) => Promise<string | null>;
    loading: boolean;
} => {
    const { getGroup } = useProctorGroups();
    const gt = GettextFactory();
    const isApplyForMeDisabled = getGroup('apply_for_me_sfm_tog')?.value === 0;

    // Preserve the legacy rba flow unless Submit for Me is explicitly disabled.
    const isAutomationLevelEnabled =
        getGroup('auto_apply_main_tog')?.value === 1 && !isApplyForMeDisabled;

    // Check if job preferences feature is enabled via proctor
    const isJobPreferencesEnabled = getGroup('ia_auto_apply_preference_m1_tst')?.value === 1;

    // Progressive-automation banner entry (RNHP homepage / MyJobs): users land
    // pre-configured and only need to review and confirm, so the flow collapses
    // to Review → Complete. Gated on the `progressive_automation` (value 2)
    // bucket of rnhp_auto_apply_banner_tst.
    const isProgressiveAutomation = getGroup('rnhp_auto_apply_banner_tst')?.value === 2;

    const flow: Flow = useMemo(() => {
        if (!isActive) {
            return INACTIVE_STUB.flow;
        }

        // Trimmed banner-entry spine: skip automation-level / job-preferences /
        // resume and start directly at Review. AutoApplySetupIndexPage redirects
        // to allSteps[0], so both homepage and MyJobs entries land on Review.
        if (isProgressiveAutomation) {
            return {
                id: 'auto-apply-setup',
                name: 'Auto Apply Setup Flow',
                description: 'Set up automatic job applications.',
                steps: [
                    {
                        // These routes render the same ReviewPage step. The base route
                        // stays primary for existing navigation. When Apply For Me is
                        // available, banner entries select the RBS or Apply For Me variant
                        // from their entry point (IAFE-2081); the disabled treatment keeps
                        // only the base and RBS routes.
                        routes: [
                            '/auto-apply-setup/review',
                            '/auto-apply-setup/review/review-before-submit',
                            ...(!isApplyForMeDisabled
                                ? ['/auto-apply-setup/review/apply-for-me']
                                : []),
                        ],
                        label: gt.gettext('Review'),
                        skipOnBack: false,
                    },
                    {
                        routes: ['/auto-apply-setup/complete'],
                        label: gt.gettext('Complete'),
                        skipOnBack: true, // Users don't navigate back from success page
                        canGoBack: false, // No back button on success page
                    },
                ],
            };
        }

        const baseSteps: FlowStep[] = [];

        // Keep automation-level on the normal spine only when its own Proctor is enabled
        // and Apply For Me is available. When the route is omitted in App, direct URLs
        // fall through to AutoApplySetupIndexPage and enter the disabled treatment.
        if (isAutomationLevelEnabled) {
            baseSteps.push({
                routes: ['/auto-apply-setup/automation-level'],
                label: gt.gettext('Automation Level'),
                skipOnBack: false,
            });
        }

        // Only include job-preferences step when proctor is enabled (before resume)
        if (isJobPreferencesEnabled) {
            baseSteps.push({
                routes: ['/auto-apply-setup/job-preferences'],
                label: gt.gettext('Job Preferences'),
                skipOnBack: false,
            });
        }

        baseSteps.push(
            {
                routes: ['/auto-apply-setup/resume'],
                label: gt.gettext('Resume'),
                skipOnBack: false,
            },
            {
                // The disabled treatment enters the explicit RBS route, so register it as
                // the same Review step for forward navigation.
                routes: [
                    '/auto-apply-setup/review',
                    ...(isApplyForMeDisabled
                        ? ['/auto-apply-setup/review/review-before-submit']
                        : []),
                ],
                label: gt.gettext('Review'),
                skipOnBack: false,
            },
            {
                routes: ['/auto-apply-setup/complete'],
                label: gt.gettext('Complete'),
                skipOnBack: true, // Users don't navigate back from success page
                canGoBack: false, // No back button on success page
            },
        );

        return {
            id: 'auto-apply-setup',
            name: 'Auto Apply Setup Flow',
            description: 'Set up automatic job applications.',
            steps: baseSteps,
        };
    }, [
        isAutomationLevelEnabled,
        isJobPreferencesEnabled,
        isProgressiveAutomation,
        isApplyForMeDisabled,
        gt,
        isActive,
    ]);

    // Edit/modal detours opened off the main flow. Built here (not stored on the
    // shared Flow type) and consumed only by getNextStep below. Exiting returns to
    // whichever route triggered the detour (dynamic origin); pages pass their own
    // fallback to exitSubFlow() on cold start / deep link.
    const subFlows = useMemo<SubFlow[]>(() => {
        const list: SubFlow[] = [
            {
                steps: [{ routes: ['/auto-apply-setup/resume/edit'], label: gt.gettext('Resume') }],
                defaultReturnRoute: '/auto-apply-setup/review',
            },
            // Mid-setup feedback: a modal detour openable from any setup
            // step. Two-step sub-flow (category → details); the details route is
            // category-parameterized, so the category page advances via
            // goForward({ params: { category } }), which getNextStep resolves here.
            {
                steps: [
                    {
                        routes: ['/auto-apply-setup/mid-setup-feedback'],
                        label: gt.gettext('Report an issue'),
                    },
                    {
                        routes: ['/auto-apply-setup/mid-setup-feedback/:category'],
                        label: gt.gettext('Feedback details'),
                    },
                ],
                defaultReturnRoute: '/auto-apply-setup',
            },
        ];
        // Keep the edit detour aligned with automation-level route availability.
        if (isAutomationLevelEnabled) {
            list.push({
                steps: [
                    {
                        routes: ['/auto-apply-setup/automation-level/edit'],
                        label: gt.gettext('Automation Level'),
                    },
                ],
                defaultReturnRoute: '/auto-apply-setup/review',
            });
        }
        return list;
    }, [isAutomationLevelEnabled, gt]);

    const getNextStep = useCallback(
        (currentRoute: string): Promise<string | null> => {
            // Pick the step list to advance through PURELY from the route: if the
            // current route belongs to a sub-flow detour, advance within that
            // sub-flow; otherwise advance the main spine. This intentionally does
            // NOT consult history depth — a detour entered cold (deep link, native
            // webview, reload) is still recognised by its route, so goForward()
            // resolves the right next step in every case. Sub-flow
            // routes are guaranteed unique vs. main steps (see SubFlowStep), so the
            // route maps to exactly one step list.
            const owningSubFlow = subFlows.find((subFlow) =>
                subFlow.steps.some((step) => stepMatchesRoute(step, currentRoute)),
            );
            const steps: Array<FlowStep | SubFlowStep> = owningSubFlow
                ? owningSubFlow.steps
                : flow.steps;
            const inSubFlow = owningSubFlow !== undefined;

            const currentIndex = steps.findIndex((step) => stepMatchesRoute(step, currentRoute));

            // Unknown route: on the main spine, enter at the first step; in a
            // sub-flow this can't happen (we matched it above to pick the list).
            if (currentIndex === -1) {
                return Promise.resolve(inSubFlow ? null : (steps[0]?.routes[0] ?? null));
            }

            // Last step → no next step. On the main spine the flow ends; in a
            // sub-flow the page then calls exitSubFlow() explicitly to leave.
            if (currentIndex === steps.length - 1) {
                return Promise.resolve(null);
            }

            return Promise.resolve(steps[currentIndex + 1].routes[0]);
        },
        [flow.steps, subFlows],
    );

    if (!isActive) {
        return INACTIVE_STUB;
    }

    return {
        flow,
        subFlows,
        getNextStep,
        loading: false,
    };
};
